import { useState } from "react";
import { Link } from "react-router-dom";
import {
  User,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  X,
  Heart,
  Briefcase,
  Star,
  Lock,
  ArrowRight,
  Plus,
  Instagram,
  Youtube,
} from "lucide-react";

const benefits = [
  {
    icon: Heart,
    title: "Paid Collaborations",
    desc: "Work on exciting paid campaigns with leading brands.",
  },
  {
    icon: Briefcase,
    title: "Brand Deals",
    desc: "Get discovered for brand partnerships that fit your niche.",
  },
  {
    icon: Star,
    title: "Long-term Partnerships",
    desc: "Build lasting relationships and grow your creator journey.",
  },
];

const CATEGORIES = [
  "Fashion",
  "Tech",
  "Food",
  "Lifestyle",
  "Travel",
  "Beauty",
  "Fitness",
  "Gaming",
  "Music",
  "Cinema",
];
const LANGUAGES = ["Telugu", "Tamil", "Kannada", "Malayalam", "Hindi", "English"];

const STEP_NUMBER_BG = "#3B82F6";

function StepHeader({ n, title }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span
        className="flex items-center justify-center w-7 h-7 rounded-full text-white text-[12px] font-bold font-heading"
        style={{ backgroundColor: STEP_NUMBER_BG }}
      >
        {n}
      </span>
      <h3
        className="font-heading font-bold text-[20px] tracking-tight"
        style={{ color: "#1E293B" }}
      >
        {title}
      </h3>
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <label
      className="block text-[13px] font-semibold mb-1.5"
      style={{ color: "#1E293B" }}
    >
      {children}
      {hint && (
        <span
          className="ml-1.5 font-normal text-[12px]"
          style={{ color: "#94A3B8" }}
        >
          {hint}
        </span>
      )}
    </label>
  );
}

function TextInput({ icon: Icon, placeholder, value, onChange, type = "text", testId }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon
          className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: "#94A3B8" }}
          strokeWidth={1.75}
        />
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-3 rounded-xl text-[14px] outline-none transition-all duration-200 focus:ring-4`}
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          color: "#1E293B",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#3B82F6";
          e.currentTarget.style.boxShadow = "0 0 0 4px rgba(59,130,246,0.12)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "#E2E8F0";
          e.currentTarget.style.boxShadow = "none";
        }}
      />
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full appearance-none px-4 py-3 rounded-xl text-[14px] outline-none transition-all duration-200 cursor-pointer"
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          color: value ? "#1E293B" : "#94A3B8",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#3B82F6";
          e.currentTarget.style.boxShadow = "0 0 0 4px rgba(59,130,246,0.12)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "#E2E8F0";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ChevronDown
        className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
        style={{ color: "#94A3B8" }}
      />
    </div>
  );
}

function MultiSelect({ options, selected, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const remaining = options.filter((o) => !selected.includes(o));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className="w-full text-left rounded-xl px-3 py-2.5 outline-none transition-all"
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          minHeight: "98px",
        }}
      >
        <div className="flex flex-wrap gap-2 pr-6">
          {selected.length === 0 && (
            <span
              className="text-[14px] py-1"
              style={{ color: "#94A3B8" }}
            >
              {placeholder}
            </span>
          )}
          {selected.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[13px] font-medium"
              style={{
                backgroundColor: "#F1F5F9",
                color: "#1E293B",
              }}
            >
              {s}
              <X
                className="w-3 h-3 cursor-pointer"
                style={{ color: "#94A3B8" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(selected.filter((x) => x !== s));
                }}
              />
            </span>
          ))}
        </div>
        <ChevronDown
          className="absolute right-3.5 top-3 w-4 h-4"
          style={{ color: "#94A3B8" }}
        />
      </button>

      {open && remaining.length > 0 && (
        <div
          className="absolute z-20 left-0 right-0 mt-1 rounded-xl p-2 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] max-h-56 overflow-auto"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
          }}
        >
          {remaining.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => {
                onChange([...selected, o]);
                if (remaining.length === 1) setOpen(false);
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-[13.5px] font-medium hover:bg-slate-50 transition-colors flex items-center gap-2"
              style={{ color: "#1E293B" }}
            >
              <Plus className="w-3.5 h-3.5" style={{ color: "#3B82F6" }} />
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function JoinCreator() {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    gender: "",
    city: "",
    categories: ["Fashion", "Tech", "Food", "Lifestyle"],
    languages: ["Telugu", "Tamil", "Kannada", "Malayalam"],
    instagram: "",
    youtube: "",
  });

  const update = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const onSubmit = (e) => {
    e.preventDefault();
    // Form is UI-only at this stage — data captured in `form` state
    console.log("Apply as Creator:", form);
  };

  return (
    <main
      data-testid="join-creator-page"
      className="min-h-screen"
      style={{ backgroundColor: "#F8FAFC" }}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-8 md:py-10">
        {/* Logo */}
        <Link
          to="/"
          data-testid="join-logo-link"
          className="inline-block font-heading font-extrabold text-[26px] md:text-[28px] tracking-tight"
          style={{ color: "#1E293B" }}
        >
          affix
          <span
            className="inline-block w-1.5 h-1.5 rounded-full ml-0.5 align-baseline"
            style={{ backgroundColor: "#3B82F6" }}
          />
        </Link>

        <div className="mt-8 md:mt-12 grid lg:grid-cols-[0.95fr_1.15fr] gap-12 lg:gap-20 items-start">
          {/* === LEFT === */}
          <div className="lg:sticky lg:top-10">
            {/* Eyebrow pill */}
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
              style={{
                backgroundColor: "rgba(59,130,246,0.1)",
                border: "1px solid rgba(59,130,246,0.18)",
              }}
            >
              <Plus className="w-3 h-3" style={{ color: "#3B82F6" }} />
              <span
                className="text-[10.5px] tracking-[0.2em] uppercase font-bold"
                style={{ color: "#3B82F6" }}
              >
                Creator Network
              </span>
            </div>

            <h1
              className="mt-6 font-heading font-extrabold tracking-tight leading-[1] text-[44px] sm:text-[56px] lg:text-[64px]"
              style={{ color: "#1E293B" }}
            >
              Join the
              <br />
              <span style={{ color: "#3B82F6" }}>Affix Creator</span>
              <br />
              Network<span style={{ color: "#3B82F6" }}>.</span>
            </h1>

            <p
              className="mt-6 max-w-md text-[15px] leading-relaxed"
              style={{ color: "#64748B" }}
            >
              Collaborate with top brands, grow your influence, and turn your
              content into meaningful opportunities.
            </p>

            <ul className="mt-10 space-y-6">
              {benefits.map((b) => {
                const Icon = b.icon;
                return (
                  <li key={b.title} className="flex items-start gap-4">
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{
                        backgroundColor: "rgba(59,130,246,0.1)",
                        color: "#3B82F6",
                      }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div>
                      <div
                        className="font-heading font-bold text-[16px] leading-tight"
                        style={{ color: "#1E293B" }}
                      >
                        {b.title}
                      </div>
                      <p
                        className="mt-1 text-[13.5px] leading-relaxed max-w-[280px]"
                        style={{ color: "#64748B" }}
                      >
                        {b.desc}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* === RIGHT — form card === */}
          <div
            className="rounded-3xl p-6 md:p-10"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              boxShadow: "0 30px 60px -30px rgba(15,23,42,0.12)",
            }}
          >
            <form onSubmit={onSubmit} className="space-y-10">
              {/* === 1. BASIC INFO === */}
              <section>
                <StepHeader n={1} title="Basic Info" />
                <hr style={{ borderColor: "#E2E8F0", marginBottom: "20px" }} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                      icon={User}
                      placeholder="Enter your full name"
                      value={form.name}
                      onChange={update("name")}
                      testId="cf-name"
                    />
                  </div>
                  <div>
                    <FieldLabel>Mobile Number</FieldLabel>
                    <TextInput
                      icon={Phone}
                      placeholder="Enter mobile number"
                      value={form.mobile}
                      onChange={update("mobile")}
                      type="tel"
                      testId="cf-mobile"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel>Email</FieldLabel>
                    <TextInput
                      icon={Mail}
                      placeholder="Enter your email address"
                      value={form.email}
                      onChange={update("email")}
                      type="email"
                      testId="cf-email"
                    />
                  </div>
                </div>
              </section>

              {/* === 2. PROFILE === */}
              <section>
                <StepHeader n={2} title="Profile" />
                <hr style={{ borderColor: "#E2E8F0", marginBottom: "20px" }} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Gender</FieldLabel>
                    <SelectInput
                      value={form.gender}
                      onChange={update("gender")}
                      options={["Male", "Female", "Non-binary", "Prefer not to say"]}
                      placeholder="Select gender"
                      testId="cf-gender"
                    />
                  </div>
                  <div>
                    <FieldLabel>City</FieldLabel>
                    <TextInput
                      icon={MapPin}
                      placeholder="Enter your city"
                      value={form.city}
                      onChange={update("city")}
                      testId="cf-city"
                    />
                  </div>
                </div>
              </section>

              {/* === 3. CREATOR DETAILS === */}
              <section>
                <StepHeader n={3} title="Creator Details" />
                <hr style={{ borderColor: "#E2E8F0", marginBottom: "20px" }} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel hint="(Select all that apply)">
                      Category
                    </FieldLabel>
                    <MultiSelect
                      options={CATEGORIES}
                      selected={form.categories}
                      onChange={update("categories")}
                      placeholder="Select categories"
                      testId="cf-categories"
                    />
                  </div>
                  <div>
                    <FieldLabel hint="(Select all that apply)">
                      Language
                    </FieldLabel>
                    <MultiSelect
                      options={LANGUAGES}
                      selected={form.languages}
                      onChange={update("languages")}
                      placeholder="Select languages"
                      testId="cf-languages"
                    />
                  </div>
                </div>
              </section>

              {/* === 4. SOCIAL LINKS === */}
              <section>
                <StepHeader n={4} title="Social Links" />
                <hr style={{ borderColor: "#E2E8F0", marginBottom: "20px" }} />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Instagram Profile Link</FieldLabel>
                    <TextInput
                      icon={Instagram}
                      placeholder="https://instagram.com/yourusername"
                      value={form.instagram}
                      onChange={update("instagram")}
                      type="url"
                      testId="cf-instagram"
                    />
                  </div>
                  <div>
                    <FieldLabel>YouTube Channel Link</FieldLabel>
                    <TextInput
                      icon={Youtube}
                      placeholder="https://youtube.com/@yourchannel"
                      value={form.youtube}
                      onChange={update("youtube")}
                      type="url"
                      testId="cf-youtube"
                    />
                  </div>
                </div>
              </section>

              {/* === Submit === */}
              <div>
                <button
                  type="submit"
                  data-testid="cf-submit"
                  className="group w-full flex items-center justify-center gap-3 rounded-xl py-4 text-white font-heading font-bold text-[16px] tracking-tight transition-all duration-300"
                  style={{
                    backgroundColor: "#3B82F6",
                    boxShadow: "0 16px 40px -16px rgba(59,130,246,0.55)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#2563EB";
                    e.currentTarget.style.boxShadow =
                      "0 24px 50px -16px rgba(59,130,246,0.75)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#3B82F6";
                    e.currentTarget.style.boxShadow =
                      "0 16px 40px -16px rgba(59,130,246,0.55)";
                  }}
                >
                  Apply as Creator
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>

                <div
                  className="mt-4 flex items-center justify-center gap-2 text-[12.5px]"
                  style={{ color: "#94A3B8" }}
                >
                  <Lock className="w-3.5 h-3.5" />
                  <span>Your information is safe with us. We'll never share it.</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
